/**
 * MemoryService - Mem0 API Integration
 * Manages user memories through the Mem0 intelligent memory layer
 * Provides semantic search, auto-categorization, and workspace associations
 *
 * NOTE: Memory system is currently DISABLED
 */

import { API_BASE_URL } from "@/lib/constants/app";
import { getSession } from "@/lib/supabase";

// Memory system disabled - set to true to re-enable
const MEMORY_ENABLED = false;

/**
 * Available memory categories
 */
export const MEMORY_CATEGORIES = [
  { value: "general", label: "General" },
  { value: "profile", label: "Profile & Preferences" },
  { value: "project", label: "Project Context" },
  { value: "chat", label: "Conversation" },
  { value: "preference", label: "Preference" },
  { value: "constraint", label: "Constraint" },
  { value: "goal", label: "Goal" },
];

/**
 * Available importance levels
 */
export const MEMORY_IMPORTANCE = [
  { value: "low", label: "Low", color: "gray" },
  { value: "medium", label: "Medium", color: "yellow" },
  { value: "high", label: "High", color: "red" },
];

class MemoryService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Get authorization headers with current session token
   * @private
   * @returns {Promise<Object>} Headers object with Content-Type and Authorization
   */
  async _getAuthHeaders() {
    const headers = { "Content-Type": "application/json" };
    try {
      const session = await getSession();
      if (session?.access_token) {
        headers["Authorization"] = `Bearer ${session.access_token}`;
      }
    } catch (error) {
      console.warn("MemoryService: Failed to get auth session", error);
    }
    return headers;
  }

  /**
   * Fetch memories for a user from Mem0 with server-side filtering and pagination
   * @param {string} userId - User ID to fetch memories for
   * @param {Object} options - Optional filters and pagination
   * @param {string} options.workspace_id - Filter by workspace
   * @param {boolean} options.include_global - Include global memories when filtering by workspace
   * @param {string} options.category - Filter by category
   * @param {string} options.importance - Filter by importance
   * @param {number} options.limit - Max results to return (default: 50)
   * @param {number} options.offset - Offset for pagination (default: 0)
   * @param {boolean} options.returnFullResponse - Return full response including pagination metadata
   * @returns {Promise<Array|Object>} Array of memories or full response object
   */
  async getUserMemories(userId, options = {}) {
    // Memory system disabled
    if (!MEMORY_ENABLED) {
      return options.returnFullResponse
        ? { memories: [], totalCount: 0, hasMore: false }
        : [];
    }

    if (!userId) {
      console.warn("MemoryService.getUserMemories: No userId provided");
      return options.returnFullResponse
        ? { memories: [], totalCount: 0, hasMore: false }
        : [];
    }

    try {
      // Build query params for server-side filtering
      const params = new URLSearchParams();
      if (options.category && options.category !== "all") {
        params.set("category", options.category);
      }
      if (options.workspace_id) {
        params.set("workspace_id", options.workspace_id);
      }
      // Include global memories when filtering by workspace (for workspace + global retrieval)
      if (options.include_global) {
        params.set("include_global", "true");
      }
      if (options.importance) {
        params.set("importance", options.importance);
      }
      if (options.limit) {
        params.set("limit", options.limit.toString());
      }
      if (options.offset) {
        params.set("offset", options.offset.toString());
      }

      const queryString = params.toString();
      const url = `${this.baseUrl}/api/ai/rag/memory/user/${userId}${queryString ? `?${queryString}` : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: await this._getAuthHeaders(),
      });

      if (!response.ok) {
        console.error(
          `MemoryService.getUserMemories: API returned ${response.status}`
        );
        return options.returnFullResponse
          ? { memories: [], totalCount: 0, hasMore: false }
          : [];
      }

      const data = await response.json();

      // Return full response if requested (for pagination UI)
      if (options.returnFullResponse) {
        return {
          memories: data.memories || [],
          totalCount: data.totalCount || 0,
          hasMore: data.hasMore || false,
          limit: data.limit,
          offset: data.offset,
          source: data.source,
        };
      }

      return data.memories || [];
    } catch (error) {
      console.error("MemoryService.getUserMemories failed:", error);
      return options.returnFullResponse
        ? { memories: [], totalCount: 0, hasMore: false }
        : [];
    }
  }

  /**
   * Convenience method to get memories for a specific workspace
   * @param {string} userId - User ID
   * @param {string} workspaceId - Workspace ID to filter by
   * @param {Object} options - Additional options (limit, offset)
   * @returns {Promise<Array>} Array of workspace memories
   */
  async getWorkspaceMemories(userId, workspaceId, options = {}) {
    if (!userId || !workspaceId) {
      console.warn(
        "MemoryService.getWorkspaceMemories: Missing userId or workspaceId"
      );
      return [];
    }

    return this.getUserMemories(userId, { workspace_id: workspaceId, ...options });
  }

  /**
   * Search memories using Mem0's semantic search
   * @param {string} userId - User ID
   * @param {string} query - Natural language search query
   * @param {Object} options - Search options
   * @param {number} options.limit - Maximum results (default: 10)
   * @param {string} options.workspaceId - Filter by workspace
   * @param {string} options.category - Filter by category
   * @returns {Promise<Array>} Array of matching memory objects
   */
  async searchMemories(userId, query, options = {}) {
    // Memory system disabled
    if (!MEMORY_ENABLED) return [];

    if (!userId || !query) {
      console.warn("MemoryService.searchMemories: Missing userId or query");
      return [];
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/search`,
        {
          method: "POST",
          headers: await this._getAuthHeaders(),
          body: JSON.stringify({
            userId,
            query,
            limit: options.limit || 10,
            filters: {
              category: options.category,
            },
            // Pass workspace_id for server-side filtering
            // When provided: returns workspace + optionally global memories
            // When null/undefined: returns only global memories (excludes workspace-scoped)
            workspace_id: options.workspace_id || options.workspaceId || null,
            include_global: options.include_global || false,
          }),
        }
      );

      if (!response.ok) {
        console.error(
          `MemoryService.searchMemories: API returned ${response.status}`
        );
        return [];
      }

      const data = await response.json();
      let memories = data.memories || [];

      // Apply workspace filter client-side if needed
      if (options.workspaceId) {
        memories = memories.filter(
          (m) => m.metadata?.workspace_id === options.workspaceId
        );
      }

      return memories;
    } catch (error) {
      console.error("MemoryService.searchMemories failed:", error);
      return [];
    }
  }

  /**
   * Create a new memory with full metadata support
   * @param {string} userId - User ID
   * @param {string} content - Memory content
   * @param {Object} metadata - Memory metadata
   * @param {string} metadata.workspaceId - Associated workspace
   * @param {string} metadata.threadId - Source thread
   * @param {string} metadata.category - Memory category
   * @param {string} metadata.importance - Importance level
   * @param {string} metadata.source - How memory was created
   * @param {string} metadata.title - Display title
   * @param {string} metadata.messageId - Source message ID
   * @param {string} metadata.messageRole - Role of source message
   * @returns {Promise<Object|null>} Created memory or null on error
   */
  async createMemory(userId, content, metadata = {}) {
    // Memory system disabled
    if (!MEMORY_ENABLED) return null;

    if (!userId || !content) {
      console.warn("MemoryService.createMemory: Missing userId or content");
      return null;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/create`,
        {
          method: "POST",
          headers: await this._getAuthHeaders(),
          body: JSON.stringify({
            userId,
            content,
            category: metadata.category || "chat",
            importance: metadata.importance || "medium",
            metadata: {
              workspace_id: metadata.workspaceId || null,
              thread_id: metadata.threadId || null,
              source: metadata.source || "manual",
              title: metadata.title || this._generateTitle(content),
              message_id: metadata.messageId || null,
              message_role: metadata.messageRole || null,
              vector_store_id: metadata.vectorStoreId || null,
              openai_file_id: metadata.openaiFileId || null,
              created_at: new Date().toISOString(),
            },
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          `MemoryService.createMemory: API Error (${response.status}):`,
          errorData
        );
        return null;
      }

      const data = await response.json();

      // Return the first created memory or the response itself
      if (data.memories && data.memories.length > 0) {
        return data.memories[0];
      }

      return data;
    } catch (error) {
      console.error("MemoryService.createMemory failed:", error);
      return null;
    }
  }

  /**
   * Update a memory's content and metadata
   * @param {string} memoryId - Memory ID to update
   * @param {string} content - New content
   * @param {Object} metadata - Updated metadata
   * @param {string} userId - User ID (required by backend)
   * @returns {Promise<Object|null>} Updated memory or null on error
   */
  async updateMemory(memoryId, content, metadata = {}, userId) {
    // Memory system disabled
    if (!MEMORY_ENABLED) return null;

    if (!memoryId) {
      console.warn("MemoryService.updateMemory: Missing memoryId");
      return null;
    }

    try {
      const body = {
        userId,
        ...(content && { newContent: content }),
        metadata: {
          ...metadata,
          updated_at: new Date().toISOString(),
        },
      };

      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/${memoryId}`,
        {
          method: "PUT",
          headers: await this._getAuthHeaders(),
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          `MemoryService.updateMemory: API Error (${response.status}):`,
          errorData
        );
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error("MemoryService.updateMemory failed:", error);
      return null;
    }
  }

  /**
   * Check if memories exist (haven't been deleted)
   * @param {Array<string>} memoryIds - Array of memory IDs to check
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Map of memoryId -> boolean (true = exists, false = deleted)
   */
  async checkMemoriesExist(memoryIds, userId) {
    if (!memoryIds || memoryIds.length === 0 || !userId) {
      return {};
    }

    try {
      // Fetch all user memories and check which IDs exist
      const result = await this.getUserMemories(userId, {
        limit: 1000, // Get a large batch to compare
        returnFullResponse: true,
      });

      const existingIds = new Set((result.memories || []).map((m) => m.id));
      const statusMap = {};

      for (const id of memoryIds) {
        statusMap[id] = existingIds.has(id);
      }

      return statusMap;
    } catch (error) {
      console.error("MemoryService.checkMemoriesExist failed:", error);
      // Return all as unknown (assume they exist)
      const statusMap = {};
      for (const id of memoryIds) {
        statusMap[id] = true;
      }
      return statusMap;
    }
  }

  /**
   * Delete a single memory
   * @param {string} memoryId - Memory ID to delete
   * @param {string} userId - User ID (required by backend)
   * @returns {Promise<boolean>} True if successful
   */
  async deleteMemory(memoryId, userId) {
    // Memory system disabled
    if (!MEMORY_ENABLED) return true;

    if (!memoryId) {
      console.warn("MemoryService.deleteMemory: Missing memoryId");
      return false;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/${memoryId}`,
        {
          method: "DELETE",
          headers: await this._getAuthHeaders(),
          body: JSON.stringify({ userId }),
        }
      );

      return response.ok;
    } catch (error) {
      console.error("MemoryService.deleteMemory failed:", error);
      return false;
    }
  }

  /**
   * Delete multiple memories in bulk
   * @param {Array<string>} memoryIds - Array of memory IDs to delete
   * @param {string} userId - User ID
   * @returns {Promise<{success: number, failed: number}>} Result counts
   */
  async deleteMemories(memoryIds, userId) {
    // Memory system disabled
    if (!MEMORY_ENABLED) return { success: 0, failed: 0 };

    if (!memoryIds || memoryIds.length === 0) {
      console.warn("MemoryService.deleteMemories: No memoryIds provided");
      return { success: 0, failed: 0 };
    }

    let success = 0;
    let failed = 0;

    // Delete in parallel with Promise.allSettled
    const results = await Promise.allSettled(
      memoryIds.map((id) => this.deleteMemory(id, userId))
    );

    results.forEach((result) => {
      if (result.status === "fulfilled" && result.value) {
        success++;
      } else {
        failed++;
      }
    });

    return { success, failed };
  }

  /**
   * Delete all memories for a user
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} True if successful
   */
  async deleteAllUserMemories(userId) {
    // Memory system disabled
    if (!MEMORY_ENABLED) return true;

    if (!userId) {
      console.warn("MemoryService.deleteAllUserMemories: No userId provided");
      return false;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/user/${userId}/all`,
        {
          method: "DELETE",
          headers: await this._getAuthHeaders(),
        }
      );

      return response.ok;
    } catch (error) {
      console.error("MemoryService.deleteAllUserMemories failed:", error);
      return false;
    }
  }

  /**
   * Analyze messages for important information to save as memories
   * Uses AI to extract preferences, goals, constraints, etc.
   * @param {string} userId - User ID
   * @param {Array} messages - Array of message objects with role and content
   * @param {Object} options - Analysis options
   * @param {string} options.workspaceId - Associate extracted memories with workspace
   * @param {string} options.threadId - Associate with thread
   * @returns {Promise<Object>} Analysis result with extracted memories
   */
  async analyzeMessages(userId, messages, options = {}) {
    // Memory system disabled
    if (!MEMORY_ENABLED) return { shouldSave: false, memories: [] };

    if (!userId || !messages || messages.length === 0) {
      console.warn(
        "MemoryService.analyzeMessages: Missing userId or messages"
      );
      return { shouldSave: false, memories: [] };
    }

    try {
      // Extract user messages for analysis
      const userMessages = messages
        .filter((m) => m.role === "user")
        .map((m) => m.content)
        .join("\n\n");

      if (!userMessages.trim()) {
        return { shouldSave: false, memories: [] };
      }

      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/analyze`,
        {
          method: "POST",
          headers: await this._getAuthHeaders(),
          body: JSON.stringify({
            userId,
            userMessage: userMessages,
            context: options.context || "",
            workspaceId: options.workspaceId || null,
            threadId: options.threadId || null,
          }),
        }
      );

      if (!response.ok) {
        console.error(
          `MemoryService.analyzeMessages: API returned ${response.status}`
        );
        return { shouldSave: false, memories: [] };
      }

      const data = await response.json();

      // Note: workspaceId is now sent in the initial request, so memories are created
      // with the correct workspace_id. This update is a fallback for backward compatibility
      // in case the backend doesn't set workspace_id during creation.
      // Skip memories without an id (Mem0 v2 returns event_id for async/queued memories)
      if (data.memories && data.memories.length > 0 && options.workspaceId) {
        // Check if any memories are missing workspace_id and have a valid id
        const memoriesNeedingUpdate = data.memories.filter(
          (memory) => memory.id && !memory.metadata?.workspace_id
        );
        if (memoriesNeedingUpdate.length > 0) {
          await Promise.all(
            memoriesNeedingUpdate.map((memory) =>
              this.updateMemory(
                memory.id,
                null,
                {
                  workspace_id: options.workspaceId,
                  thread_id: options.threadId,
                },
                userId
              )
            )
          );
        }
      }

      return {
        shouldSave: data.shouldSave || false,
        memories: data.memories || [],
        analysis: data.analysis || null,
      };
    } catch (error) {
      console.error("MemoryService.analyzeMessages failed:", error);
      return { shouldSave: false, memories: [] };
    }
  }

  /**
   * Create multiple memories in a batch operation
   * @param {string} userId - User ID
   * @param {Array<Object>} memories - Array of memory objects with content and metadata
   * @returns {Promise<Object>} Batch result with created/skipped/failed counts
   */
  async createMemoriesBatch(userId, memories) {
    // Memory system disabled
    if (!MEMORY_ENABLED) return { success: true, total: 0, created: 0, skipped: 0, failed: 0 };

    if (!userId || !memories || memories.length === 0) {
      console.warn("MemoryService.createMemoriesBatch: Missing userId or memories");
      return { success: false, total: 0, created: 0, skipped: 0, failed: 0 };
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/batch`,
        {
          method: "POST",
          headers: await this._getAuthHeaders(),
          body: JSON.stringify({ userId, memories }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          `MemoryService.createMemoriesBatch: API Error (${response.status}):`,
          errorData
        );
        return { success: false, total: memories.length, created: 0, skipped: 0, failed: memories.length };
      }

      return await response.json();
    } catch (error) {
      console.error("MemoryService.createMemoriesBatch failed:", error);
      return { success: false, total: memories.length, created: 0, skipped: 0, failed: memories.length };
    }
  }

  /**
   * Get memory system analytics
   * @returns {Promise<Object>} Analytics data
   */
  async getAnalytics() {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/analytics`,
        {
          method: "GET",
          headers: await this._getAuthHeaders(),
        }
      );

      if (!response.ok) {
        console.error(
          `MemoryService.getAnalytics: API returned ${response.status}`
        );
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error("MemoryService.getAnalytics failed:", error);
      return null;
    }
  }

  /**
   * Get memory system status (circuit breaker, fallback)
   * @returns {Promise<Object>} Status data
   */
  async getStatus() {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/status`,
        {
          method: "GET",
          headers: await this._getAuthHeaders(),
        }
      );

      if (!response.ok) {
        console.error(
          `MemoryService.getStatus: API returned ${response.status}`
        );
        return { health: "unknown" };
      }

      return await response.json();
    } catch (error) {
      console.error("MemoryService.getStatus failed:", error);
      return { health: "unknown", error: error.message };
    }
  }

  /**
   * Trigger sync of pending fallback memories to Mem0
   * @returns {Promise<Object>} Sync result
   */
  async syncFallbackMemories() {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/sync`,
        {
          method: "POST",
          headers: await this._getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          `MemoryService.syncFallbackMemories: API Error (${response.status}):`,
          errorData
        );
        return { success: false, error: errorData.message };
      }

      return await response.json();
    } catch (error) {
      console.error("MemoryService.syncFallbackMemories failed:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check memory service health
   * @returns {Promise<Object>} Health status
   */
  async checkHealth() {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/rag/memory/health`,
        {
          method: "GET",
          headers: await this._getAuthHeaders(),
        }
      );

      if (!response.ok) {
        return { healthy: false, status: response.status };
      }

      const data = await response.json();
      return {
        healthy: data.status === "healthy",
        ...data,
      };
    } catch (error) {
      console.error("MemoryService.checkHealth failed:", error);
      return { healthy: false, error: error.message };
    }
  }

  /**
   * Get available memory categories
   * @returns {Array} List of category objects
   */
  getCategories() {
    return MEMORY_CATEGORIES;
  }

  /**
   * Get available importance levels
   * @returns {Array} List of importance objects
   */
  getImportanceLevels() {
    return MEMORY_IMPORTANCE;
  }

  /**
   * Generate a simple title from content
   * @private
   * @param {string} content - Memory content
   * @returns {string} Generated title
   */
  _generateTitle(content) {
    if (!content) return "Untitled Memory";

    const firstSentence =
      content.split(/[.!?]/).filter((s) => s.trim().length > 0)[0] || "";
    return firstSentence.trim().substring(0, 50) || "Untitled Memory";
  }
}

// Create singleton instance
const memoryService = new MemoryService();

// Export both the singleton and individual functions for backwards compatibility
export const getUserMemories = (userId, options) =>
  memoryService.getUserMemories(userId, options);
export const getWorkspaceMemories = (userId, workspaceId) =>
  memoryService.getWorkspaceMemories(userId, workspaceId);
export const searchMemories = (userId, query, options) =>
  memoryService.searchMemories(userId, query, options);
export const createMemory = (userId, content, metadata) =>
  memoryService.createMemory(userId, content, metadata);
export const createMemoriesBatch = (userId, memories) =>
  memoryService.createMemoriesBatch(userId, memories);
export const updateMemory = (memoryId, content, metadata, userId) =>
  memoryService.updateMemory(memoryId, content, metadata, userId);
export const deleteMemory = (memoryId, userId) =>
  memoryService.deleteMemory(memoryId, userId);
export const deleteMemories = (memoryIds, userId) =>
  memoryService.deleteMemories(memoryIds, userId);
export const checkMemoriesExist = (memoryIds, userId) =>
  memoryService.checkMemoriesExist(memoryIds, userId);
export const deleteAllUserMemories = (userId) =>
  memoryService.deleteAllUserMemories(userId);
export const analyzeMessages = (userId, messages, options) =>
  memoryService.analyzeMessages(userId, messages, options);
export const checkMemoryServiceHealth = () => memoryService.checkHealth();
export const getMemoryAnalytics = () => memoryService.getAnalytics();
export const getMemoryStatus = () => memoryService.getStatus();
export const syncFallbackMemories = () => memoryService.syncFallbackMemories();

export default memoryService;
